import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";

/**
 * Three numbers, three ways to read them: what the client paid, what it cost in
 * employee pay, and what's left. Each one month by month, averaged per month,
 * and summed over all time.
 *
 * The endpoint also serves `period`, `employee_id`, `clients` and
 * `employee_rows`, which this page doesn't render.
 *
 * By default settled money only: a milestone counts in the month the client
 * actually paid for it. The "Include unpaid" box switches the endpoint to
 * `mode=projected`, which also counts milestones the client hasn't paid yet,
 * in the month they were created (the only date an unpaid one has).
 * Everything is USD, converted at the rate on that date.
 *
 * Two checkbox filters sit above it, for which clients' money is counted and
 * whose pay counts as employee cost. They cut in different places: a client
 * filter drops whole milestones so every column moves, while an employee filter
 * only changes what lands in the Employees column, and Profit follows it so the
 * rows keep adding up.
 */

// What's remembered is who you switched OFF, not who's on. Store the checked
// ids instead and an employee hired next month would silently not be counted,
// because they wouldn't be in the list you saved today.
const EXCLUDED_KEYS = { employees: "analytics.excluded.employees", clients: "analytics.excluded.clients" };

// localStorage throws in a private window and comes back empty after a clear,
// so every read and write is guarded and the page renders fine without it.
const readExcluded = (which) => {
  try {
    const raw = window.localStorage.getItem(EXCLUDED_KEYS[which]);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map(Number).filter(Number.isFinite) : [];
  } catch {
    return [];
  }
};

const writeExcluded = (which, ids) => {
  try {
    window.localStorage.setItem(EXCLUDED_KEYS[which], JSON.stringify([...ids]));
  } catch {
    /* not being able to remember the choice is not a reason to break the page */
  }
};

// Per-viewer convenience, same guarding as the exclusions above.
const UNPAID_KEY = "analytics.includeUnpaid";
const readIncludeUnpaid = () => {
  try {
    return window.localStorage.getItem(UNPAID_KEY) === "1";
  } catch {
    return false;
  }
};
const writeIncludeUnpaid = (on) => {
  try {
    window.localStorage.setItem(UNPAID_KEY, on ? "1" : "0");
  } catch {
    /* see writeExcluded */
  }
};

// How far back the table goes, in calendar months (the current one included).
// null = all time. The server applies it, so the average and total rows cover
// the same window as the rows above them.
const RANGE_OPTIONS = [
  { value: 1, label: "Last 1" },
  { value: 3, label: "Last 3" },
  { value: 6, label: "Last 6" },
  { value: 12, label: "Last 12" },
  { value: null, label: "All" },
];
const RANGE_KEY = "analytics.months";
const readRange = () => {
  try {
    const n = Number(window.localStorage.getItem(RANGE_KEY));
    return RANGE_OPTIONS.some((o) => o.value === n) ? n : null;
  } catch {
    return null;
  }
};
const writeRange = (n) => {
  try {
    window.localStorage.setItem(RANGE_KEY, n == null ? "" : String(n));
  } catch {
    /* see writeExcluded */
  }
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "2026-05" -> "May 2026"
const fmtMonth = (key) => {
  const [y, m] = String(key || "").split("-");
  const i = Number(m) - 1;
  return MONTHS[i] ? `${MONTHS[i]} ${y}` : key;
};

// Thousands separators, and cents only when there are any: these are sums read
// at a glance, so "$12,400" beats "12400.00".
const money = (v) => {
  const n = Number(v || 0);
  const frac = Number.isInteger(n) ? 0 : 2;
  return `$${n.toLocaleString("en-US", {
    minimumFractionDigits: frac,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * One row of checkboxes. `options` is the full roster (never just whoever
 * survived the current filter), `excluded` the ids switched off. The last one
 * left on can't be switched off: an empty filter would mean an empty page, and
 * a blank table reads like a bug rather than a choice.
 */
function FilterRow({ label, options, excluded, onToggle }) {
  if (!options?.length) return null;
  const onCount = options.length - options.filter((o) => excluded.has(o.id)).length;
  return (
    <div className="analytics-filter">
      <span className="analytics-filter-label">{label}</span>
      {options.map((opt) => {
        const checked = !excluded.has(opt.id);
        const last = checked && onCount === 1;
        return (
          <label
            key={opt.id}
            className={`analytics-filter-opt${last ? " is-locked" : ""}`}
            title={last ? `At least one ${label.toLowerCase().replace(/s$/, "")} has to stay on` : undefined}
          >
            <input
              type="checkbox"
              className="cbx"
              checked={checked}
              disabled={last}
              onChange={() => onToggle(opt.id)}
            />
            <span>{opt.name}</span>
          </label>
        );
      })}
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [excludedEmp, setExcludedEmp] = useState(() => new Set(readExcluded("employees")));
  const [excludedCli, setExcludedCli] = useState(() => new Set(readExcluded("clients")));
  const [includeUnpaid, setIncludeUnpaid] = useState(readIncludeUnpaid);
  const [range, setRange] = useState(readRange);

  // The roster comes back with the data and isn't itself filtered, so it's
  // stable across refetches.
  // Kept apart from `data` because the filter params are built from it, and it
  // only arrives with the first response. That first request goes out with no
  // roster, so it can't apply the remembered exclusions; when the roster lands,
  // `rosterKey` changes and the effect refetches with them applied. It settles
  // after that: the roster isn't filtered, so the second answer carries the
  // same one.
  const [roster, setRoster] = useState({ employees: [], clients: [] });
  const employees = roster.employees;
  const clients = roster.clients;
  const rosterKey = useMemo(
    () => `${employees.map((e) => e.id).join(",")}|${clients.map((c) => c.id).join(",")}`,
    [employees, clients],
  );
  // Whether the current `data` was fetched with the roster known, i.e. with the
  // exclusions actually applied. Until then, showing it would show everyone.
  const [filtersApplied, setFiltersApplied] = useState(false);

  // Send ids rather than "everything except": the server only ever gets the
  // list to include, and an empty list would mean "no filter" there.
  const paramsFor = useCallback((options, excluded) => {
    const on = options.filter((o) => !excluded.has(o.id)).map((o) => o.id);
    return on.length && on.length < options.length ? on : undefined;
  }, []);

  useEffect(() => {
    setErr(null);
    api
      .get("/analytics", {
        params: {
          period: "all",
          mode: includeUnpaid ? "projected" : "past",
          months: range ?? undefined,
          employee_ids: paramsFor(employees, excludedEmp),
          client_ids: paramsFor(clients, excludedCli),
        },
        // axios defaults to employee_ids[]=1&employee_ids[]=2; FastAPI wants
        // the same key repeated without the brackets.
        paramsSerializer: { indexes: null },
      })
      .then((r) => {
        const next = { employees: r.data.employees || [], clients: r.data.client_options || [] };
        const nextKey =
          `${next.employees.map((e) => e.id).join(",")}|${next.clients.map((c) => c.id).join(",")}`;
        if (nextKey !== rosterKey) setRoster(next);
        // An empty roster has nothing to filter by, so that answer is final too.
        setFiltersApplied(rosterKey !== "|" || nextKey === "|");
        setData(r.data);
      })
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load analytics"));
    // Keyed on the roster's ids, not the arrays: a refetch returning the same
    // people must not trigger another refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludedEmp, excludedCli, includeUnpaid, range, rosterKey]);

  const toggle = (which) => (id) => {
    const [excluded, setExcludedState] = which === "employees"
      ? [excludedEmp, setExcludedEmp]
      : [excludedCli, setExcludedCli];
    const next = new Set(excluded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    writeExcluded(which, next);
    setExcludedState(next);
  };

  // Nothing excluded means the unfiltered first answer is already the right one.
  const ready = data && (filtersApplied || (excludedEmp.size === 0 && excludedCli.size === 0));
  const o = data?.overall;
  // Newest month first: the one you're living in is the one you look at.
  const months = [...(o?.monthly || [])].reverse();

  return (
    <>
      <div className="page-head">
        <div className="title-block">
          <h1>Analytics</h1>
          <div className="meta-line">
            <span className="muted-sm">
              {includeUnpaid
                ? "Paid milestones in the month they were paid, plus unpaid ones in the month they were created."
                : "Money that has actually been paid, counted in the month it was paid."}{" "}
              Milestones billed in EUR or CAD are converted to USD at the rate on
              that date.
            </span>
          </div>
        </div>
      </div>

      {err && <div className="error">{err}</div>}

      {data && (
        <div className="analytics-filters">
          <div className="analytics-filter">
            <span className="analytics-filter-label">Months</span>
            <div className="period-filter analytics-range">
              {RANGE_OPTIONS.map((opt) => (
                <button
                  key={opt.label}
                  type="button"
                  className={range === opt.value ? "active" : undefined}
                  onClick={() => {
                    writeRange(opt.value);
                    setRange(opt.value);
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <FilterRow
            label="Employees"
            options={employees}
            excluded={excludedEmp}
            onToggle={toggle("employees")}
          />
          <FilterRow
            label="Clients"
            options={clients}
            excluded={excludedCli}
            onToggle={toggle("clients")}
          />
          <div className="analytics-filter">
            <span className="analytics-filter-label">Milestones</span>
            <label
              className="analytics-filter-opt"
              title="Also count milestones the client hasn't paid yet, in the month they were created"
            >
              <input
                type="checkbox"
                className="cbx"
                checked={includeUnpaid}
                onChange={(e) => {
                  writeIncludeUnpaid(e.target.checked);
                  setIncludeUnpaid(e.target.checked);
                }}
              />
              <span>Include unpaid</span>
            </label>
            {ready && includeUnpaid && Number(o?.outstanding) > 0 && (
              <span className="muted-sm">({money(o.outstanding)} of it not collected yet)</span>
            )}
          </div>
        </div>
      )}

      {ready && months.length === 0 && (
        <div className="card pad">
          <div className="empty">
            {range ? `Nothing in the last ${range === 1 ? "month" : `${range} months`}.` : "Nothing paid yet."}
            <small>A milestone appears here once the client has paid for it.</small>
          </div>
        </div>
      )}

      {ready && months.length > 0 && (
        <div className="card pad">
          <div className="table-wrap">
            <table className="simple analytics-table" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th>Month</th>
                  <th className="num-cell">{includeUnpaid ? "Client billed" : "Client paid"}</th>
                  <th className="num-cell">Employees</th>
                  <th className="num-cell">Profit</th>
                </tr>
              </thead>
              <tbody>
                {months.map((m) => (
                  <tr key={m.month}>
                    <td>{fmtMonth(m.month)}</td>
                    <td className="num-cell">{money(m.net)}</td>
                    <td className="num-cell muted-cell">{money(m.employee_cost)}</td>
                    <td className="num-cell accent">{money(m.profit)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="analytics-summary">
                  <td>
                    Avg/month
                    <span className="muted-sm" style={{ marginLeft: 8 }}>
                      {range ? `(over the last ${range === 1 ? "month" : `${range} months`})` : "(over all time)"}
                    </span>
                  </td>
                  <td className="num-cell">{money(o.avg_net_per_month)}</td>
                  <td
                    className="num-cell muted-cell"
                    title={
                      o.employee_months === o.months
                        ? undefined
                        : `Averaged over the ${o.employee_months} month(s) with assigned work, ` +
                          `not all ${o.months}: a month with nobody assigned isn't cheap, it's empty.`
                    }
                  >
                    {money(o.avg_employee_cost_per_month)}
                    {o.employee_months !== o.months && (
                      <span className="muted-sm" style={{ marginLeft: 6 }}>
                        ({o.employee_months} mo)
                      </span>
                    )}
                  </td>
                  <td className="num-cell accent">{money(o.avg_profit_per_month)}</td>
                </tr>
                <tr className="analytics-summary total">
                  <td>
                    {range ? (range === 1 ? "This month" : `Last ${range} months`) : "All time"}
                    <span className="muted-sm" style={{ marginLeft: 8 }}>
                      {fmtMonth(o.first_month)} to {fmtMonth(o.last_month)}
                    </span>
                  </td>
                  <td className="num-cell">{money(o.net)}</td>
                  <td className="num-cell muted-cell">{money(o.employee_cost)}</td>
                  <td className="num-cell accent">{money(o.profit)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
